import "./styles.css";
import { scene } from "./scene/config";
import { cloneOverrides, emptyOverrides, parseOverrides, serializeOverrides } from "./scene/overrides";
import { SceneRenderer } from "./scene/renderer";
import type { ExperienceMode, SceneOverrides } from "./scene/types";
import { validateScene } from "./scene/validation";

const requireElement = <T extends Element>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing required element: ${selector}`);
  return element;
};

const canUseWebGL = (): boolean => {
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2") || canvas.getContext("webgl"));
  } catch {
    return false;
  }
};

const validModes = new Set<ExperienceMode>(["explore", "read", "layers"]);
const params = new URLSearchParams(window.location.search);
const requestedMode = params.get("mode") as ExperienceMode | null;
const forcedFallback = params.get("fallback") === "1";
const forcedQuality = params.get("quality");
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

document.documentElement.classList.add("js");
validateScene(scene);

const host = requireElement<HTMLElement>("#canvas-host");
const loadStatus = requireElement<HTMLElement>("[data-load-status]");
const reader = requireElement<HTMLElement>("#reader");
const inspector = requireElement<HTMLElement>("#layer-inspector");
const layersButton = requireElement<HTMLButtonElement>("[data-mode-button='layers']");
const pauseButton = requireElement<HTMLButtonElement>("[data-pause]");
const hotspotButtons = new Map(
  [...document.querySelectorAll<HTMLButtonElement>("[data-hotspot]")].map((button) => [button.dataset.hotspot ?? "", button]),
);

let renderer: SceneRenderer | null = null;
let mode: ExperienceMode = requestedMode !== null && validModes.has(requestedMode) ? requestedMode : "explore";
let paused = false;
let assembly = 1;
let soloId: string | null = null;
let overrides: SceneOverrides = emptyOverrides(scene);
let activeChapterId: string = scene.chapters[0].id;

const chapterFor = (id: string) => scene.chapters.find((chapter) => chapter.id === id) ?? scene.chapters[0];

const updateHotspots = (positions: ReadonlyMap<string, { x: number; y: number }>): void => {
  for (const [id, button] of hotspotButtons) {
    const point = positions.get(id);
    button.hidden = !point;
    if (point) button.style.transform = `translate3d(${point.x}px, ${point.y}px, 0)`;
  }
};

const setChapter = (id: string): void => {
  const chapter = chapterFor(id);
  activeChapterId = chapter.id;
  for (const element of document.querySelectorAll<HTMLElement>("[data-chapter]")) {
    element.dataset.active = String(element.dataset.chapter === activeChapterId);
  }
  renderer?.setCamera(chapter.camera);
  renderer?.setEmphasis(chapter.emphasis ? { ...chapter.emphasis } : {});
};

const updateModeAccessibility = (): void => {
  const isLayers = mode === "layers";
  reader.inert = isLayers;
  inspector.inert = !isLayers;
  reader.setAttribute("aria-hidden", String(isLayers));
  inspector.setAttribute("aria-hidden", String(!isLayers));
};

const setMode = (next: ExperienceMode, options: { focus?: boolean } = {}): void => {
  if (next === "layers" && layersButton.disabled) next = "explore";
  mode = next;
  document.body.dataset.mode = mode;
  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-mode-button]")) {
    button.setAttribute("aria-pressed", String(button.dataset.modeButton === mode));
  }
  updateModeAccessibility();
  renderer?.setFit(mode === "layers" ? "contain" : "cover");
  renderer?.setInspectorActive(mode === "layers");
  renderer?.setInspectorState(overrides, assembly, soloId);
  if (mode === "read") setChapter(activeChapterId);
  else if (mode === "layers" || window.scrollY < window.innerHeight) {
    renderer?.setEmphasis({});
    renderer?.setCamera(scene.initialCamera);
  }
  if (options.focus) {
    const target = mode === "read" ? reader : mode === "layers" ? inspector : requireElement<HTMLElement>(".explore-intro");
    target.focus({ preventScroll: true });
    if (mode === "explore") window.scrollTo({ top: 0, behavior: reduceMotion.matches ? "auto" : "smooth" });
    if (mode === "read") requireElement<HTMLElement>("#the-room").scrollIntoView({ behavior: reduceMotion.matches ? "auto" : "smooth" });
  }
  if (mode === "layers" && options.focus) {
    requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "auto" })));
  }
};

for (const button of document.querySelectorAll<HTMLButtonElement>("[data-mode-button]")) {
  button.addEventListener("click", () => setMode(button.dataset.modeButton as ExperienceMode, { focus: true }));
}

requireElement<HTMLAnchorElement>("[data-enter-read]").addEventListener("click", (event) => {
  event.preventDefault();
  setMode("read");
  requestAnimationFrame(() => requireElement<HTMLElement>("#the-room").scrollIntoView({ behavior: reduceMotion.matches ? "auto" : "smooth" }));
});

for (const hotspot of scene.hotspots) {
  const button = hotspotButtons.get(hotspot.id);
  if (!button) continue;
  button.setAttribute("aria-label", hotspot.label);
  button.addEventListener("click", () => {
    setMode("read");
    setChapter(hotspot.chapterId);
    requestAnimationFrame(() => {
      const target = document.getElementById(hotspot.chapterId);
      target?.scrollIntoView({ behavior: reduceMotion.matches ? "auto" : "smooth", block: "start" });
      target?.focus({ preventScroll: true });
    });
  });
}

const scrollArtwork = [...document.querySelectorAll<HTMLElement>("[data-scroll-art]")];
let scrollFrame = 0;
const updateScrollArtwork = (): void => {
  scrollFrame = 0;
  for (const artwork of scrollArtwork) {
    if (paused || reduceMotion.matches) {
      artwork.style.setProperty("--flow-y", "0px");
      continue;
    }
    const sceneElement = artwork.closest<HTMLElement>(".flow-scene");
    if (!sceneElement) continue;
    const rect = sceneElement.getBoundingClientRect();
    const progress = Math.max(-1, Math.min(1, (window.innerHeight / 2 - (rect.top + rect.height / 2)) / (rect.height / 2)));
    artwork.style.setProperty("--flow-y", `${progress * 34}px`);
  }
};
const requestScrollArtwork = (): void => {
  if (!scrollFrame) scrollFrame = window.requestAnimationFrame(updateScrollArtwork);
};
window.addEventListener("scroll", requestScrollArtwork, { passive: true });
window.addEventListener("resize", requestScrollArtwork, { passive: true });
requestScrollArtwork();

pauseButton.addEventListener("click", () => {
  paused = !paused;
  pauseButton.setAttribute("aria-pressed", String(paused));
  pauseButton.textContent = paused ? "Resume motion" : "Pause motion";
  renderer?.pause(paused);
  requestScrollArtwork();
});

const chapterObserver = new IntersectionObserver(
  (entries) => {
    if (mode === "layers") return;
    const active = entries
      .filter((entry) => entry.isIntersecting)
      .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
    const chapterId = (active?.target as HTMLElement | undefined)?.dataset.chapter;
    if (chapterId && chapterId !== activeChapterId) setChapter(chapterId);
  },
  { rootMargin: "-22% 0px -42%", threshold: [0.1, 0.35, 0.65] },
);
document.querySelectorAll<HTMLElement>("[data-chapter]").forEach((chapter) => chapterObserver.observe(chapter));

const layerControls = requireElement<HTMLElement>("[data-layer-controls]");
const depthControls = requireElement<HTMLElement>("[data-depth-controls]");
const assemblyInput = requireElement<HTMLInputElement>("[data-assembly]");
const assemblyOutput = requireElement<HTMLOutputElement>("[data-assembly-output]");
const jsonInput = requireElement<HTMLTextAreaElement>("[data-json-input]");
const inspectorStatus = requireElement<HTMLElement>("[data-inspector-status]");

const applyInspector = (): void => renderer?.setInspectorState(overrides, assembly, soloId);

const renderInspectorControls = (): void => {
  layerControls.replaceChildren();
  for (const layer of scene.layers) {
    const row = document.createElement("fieldset");
    row.className = "layer-row";
    const legend = document.createElement("legend");
    legend.textContent = layer.title;
    const visible = document.createElement("input");
    visible.type = "checkbox";
    visible.checked = overrides.layers[layer.id]?.visible ?? layer.visible;
    visible.setAttribute("aria-label", `Show ${layer.title}`);
    visible.addEventListener("change", () => {
      overrides.layers[layer.id] = { ...overrides.layers[layer.id], visible: visible.checked };
      applyInspector();
    });
    const opacity = document.createElement("input");
    opacity.type = "range";
    opacity.min = "0";
    opacity.max = "1";
    opacity.step = "0.01";
    opacity.value = String(overrides.layers[layer.id]?.opacity ?? layer.opacity);
    opacity.setAttribute("aria-label", `${layer.title} opacity`);
    const value = document.createElement("output");
    value.textContent = `${Math.round(Number(opacity.value) * 100)}%`;
    opacity.addEventListener("input", () => {
      const next = Number(opacity.value);
      value.textContent = `${Math.round(next * 100)}%`;
      overrides.layers[layer.id] = { ...overrides.layers[layer.id], opacity: next };
      applyInspector();
    });
    const solo = document.createElement("button");
    solo.type = "button";
    solo.textContent = "Solo";
    solo.setAttribute("aria-pressed", String(soloId === layer.id));
    solo.addEventListener("click", () => {
      soloId = soloId === layer.id ? null : layer.id;
      renderInspectorControls();
      applyInspector();
    });
    const visibilityLabel = document.createElement("label");
    visibilityLabel.append(visible, document.createTextNode(" Visible"));
    const opacityLabel = document.createElement("label");
    opacityLabel.append(document.createTextNode("Opacity "), opacity, value);
    row.append(legend, visibilityLabel, opacityLabel, solo);
    layerControls.append(row);
  }

  depthControls.replaceChildren();
  const groups = new Map(scene.layers.map((layer) => [layer.motionGroup, layer.depth]));
  for (const [group, defaultDepth] of groups) {
    const label = document.createElement("label");
    label.className = "depth-row";
    const title = document.createElement("span");
    title.textContent = `${group} depth`;
    const input = document.createElement("input");
    input.type = "range";
    input.min = "0";
    input.max = "1";
    input.step = "0.01";
    input.value = String(overrides.groupDepths[group] ?? defaultDepth);
    input.setAttribute("aria-label", `${group} group depth`);
    const output = document.createElement("output");
    output.textContent = Number(input.value).toFixed(2);
    input.addEventListener("input", () => {
      overrides.groupDepths[group] = Number(input.value);
      output.textContent = Number(input.value).toFixed(2);
      applyInspector();
    });
    label.append(title, input, output);
    depthControls.append(label);
  }
};

assemblyInput.addEventListener("input", () => {
  assembly = Number(assemblyInput.value);
  assemblyOutput.value = `${Math.round(assembly * 100)}%`;
  applyInspector();
});

requireElement<HTMLButtonElement>("[data-reset]").addEventListener("click", () => {
  overrides = emptyOverrides(scene);
  assembly = 1;
  soloId = null;
  assemblyInput.value = "1";
  assemblyOutput.value = "100%";
  jsonInput.value = "";
  inspectorStatus.textContent = "Inspector reset to the canonical scene.";
  renderInspectorControls();
  applyInspector();
});

requireElement<HTMLButtonElement>("[data-export]").addEventListener("click", async () => {
  const exported = serializeOverrides(overrides);
  jsonInput.value = exported;
  try {
    await navigator.clipboard.writeText(exported);
    inspectorStatus.textContent = "Validated overrides exported and copied.";
  } catch {
    inspectorStatus.textContent = "Validated overrides exported to the text field.";
  }
});

requireElement<HTMLButtonElement>("[data-import]").addEventListener("click", () => {
  try {
    overrides = cloneOverrides(parseOverrides(jsonInput.value, scene));
    soloId = null;
    inspectorStatus.textContent = "Overrides imported.";
    renderInspectorControls();
    applyInspector();
  } catch (error) {
    inspectorStatus.textContent = error instanceof Error ? error.message : "Overrides could not be imported.";
  }
});

renderInspectorControls();
updateModeAccessibility();
setChapter(activeChapterId);

const reducedMotionChanged = (event: MediaQueryListEvent): void => {
  renderer?.setReducedMotion(event.matches);
  requestScrollArtwork();
};
reduceMotion.addEventListener("change", reducedMotionChanged);

const quality: "low" | "high" =
  forcedQuality === "low" || (forcedQuality !== "high" && window.matchMedia("(max-width: 700px)").matches) ? "low" : "high";
document.body.dataset.quality = quality;

const startRenderer = async (): Promise<void> => {
  if (forcedFallback || !canUseWebGL()) {
    document.body.dataset.renderer = "fallback";
    layersButton.disabled = true;
    layersButton.title = "Layer inspection requires WebGL.";
    loadStatus.textContent = forcedFallback
      ? "Static fallback requested. The complete painting and story remain available."
      : "WebGL is unavailable. Showing the complete static painting.";
    setMode(mode === "layers" ? "explore" : mode);
    return;
  }

  renderer = new SceneRenderer(
    {
      document: scene,
      host,
      quality,
      reducedMotion: reduceMotion.matches,
      onHotspots: updateHotspots,
      onFailure: (message) => {
        document.body.dataset.renderer = "fallback";
        layersButton.disabled = true;
        loadStatus.textContent = message;
      },
    },
    overrides,
  );
  try {
    await renderer.initialize();
    document.body.dataset.renderer = "live";
    loadStatus.textContent = "Painting ready.";
    window.setTimeout(() => loadStatus.setAttribute("hidden", ""), 1200);
    setMode(mode);
  } catch (error) {
    renderer.destroy();
    renderer = null;
    document.body.dataset.renderer = "fallback";
    layersButton.disabled = true;
    loadStatus.textContent = error instanceof Error ? `${error.message} Showing the complete static painting.` : "The live painting could not start. Showing the static version.";
    setMode(mode === "layers" ? "explore" : mode);
  }
};

void startRenderer();

window.addEventListener(
  "pagehide",
  () => {
    chapterObserver.disconnect();
    window.removeEventListener("scroll", requestScrollArtwork);
    window.removeEventListener("resize", requestScrollArtwork);
    if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
    reduceMotion.removeEventListener("change", reducedMotionChanged);
    renderer?.destroy();
    renderer = null;
  },
  { once: true },
);
